import { motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const FG_SIZE = 8;
const BG_SIZE = 9;
const MARGIN = 15;

//Based on react-portal-tooltip
const Card = ({
  position = "bottom",
  arrow = "center",
  align = "center",
  style = {},
  arrowStyle = {},
  parentEl,
  children
}) => {
  const [pos, setPos] = useState({ width: 0, height: 0 });

  const rootRef = useRef();

  const mergeStyle = (_style, theme) => {
    if (theme) {
      const {
        /*position,
        top,
        left,
        right,
        bottom,
        marginLeft,
        marginRight,*/
        ...validTheme
      } = theme;

      return {
        ..._style,
        ...validTheme
      };
    }

    return _style;
  };

  const getArrowStyle = useCallback(() => {
    const baseArrowStyle = {
      position: "absolute",
      content: '""'
    };

    let fgStyle = { ...baseArrowStyle };
    let bgStyle = { ...baseArrowStyle };
    fgStyle.zIndex = 60;
    bgStyle.zIndex = 55;

    const defaultArrowStyle = {
      color: "#fff",
      borderColor: "transparent"
    };

    let tArrowStyle = {
      ...defaultArrowStyle,
      ...arrowStyle
    };
    const bgBorderColor = tArrowStyle.borderColor
      ? tArrowStyle.borderColor
      : "transparent";

    const fgColorBorder = `10px solid ${tArrowStyle.color}`;
    const fgTransBorder = `${FG_SIZE}px solid transparent`;
    const bgColorBorder = `11px solid ${bgBorderColor}`;
    const bgTransBorder = `${BG_SIZE}px solid transparent`;

    if (position === "left" || position === "right") {
      fgStyle.top = "50%";
      fgStyle.borderTop = fgTransBorder;
      fgStyle.borderBottom = fgTransBorder;
      fgStyle.marginTop = -7;

      bgStyle.borderTop = bgTransBorder;
      bgStyle.borderBottom = bgTransBorder;
      bgStyle.top = "50%";
      bgStyle.marginTop = -8;

      if (position === "left") {
        fgStyle.right = -10;
        fgStyle.borderLeft = fgColorBorder;
        bgStyle.right = -11;
        bgStyle.borderLeft = bgColorBorder;
      } else {
        fgStyle.left = -10;
        fgStyle.borderRight = fgColorBorder;
        bgStyle.left = -11;
        bgStyle.borderRight = bgColorBorder;
      }

      if (arrow === "top") {
        fgStyle.top = MARGIN;
        bgStyle.top = MARGIN;
      }
      if (arrow === "bottom") {
        fgStyle.top = null;
        fgStyle.bottom = MARGIN - 7;
        bgStyle.top = null;
        bgStyle.bottom = MARGIN - 8;
      }
    } else {
      fgStyle.left = Math.round(pos.width / 2 - FG_SIZE);
      fgStyle.borderLeft = fgTransBorder;
      fgStyle.borderRight = fgTransBorder;
      fgStyle.marginLeft = 0;
      bgStyle.left = fgStyle.left - 1;
      bgStyle.borderLeft = bgTransBorder;
      bgStyle.borderRight = bgTransBorder;
      bgStyle.marginLeft = 0;

      if (position === "top") {
        fgStyle.bottom = -10;
        fgStyle.borderTop = fgColorBorder;
        bgStyle.bottom = -11;
        bgStyle.borderTop = bgColorBorder;
      } else {
        fgStyle.top = -10;
        fgStyle.borderBottom = fgColorBorder;
        bgStyle.top = -11;
        bgStyle.borderBottom = bgColorBorder;
      }

      if (arrow === "right") {
        fgStyle.left = null;
        fgStyle.right = MARGIN + 1 - FG_SIZE;
        bgStyle.left = null;
        bgStyle.right = MARGIN - FG_SIZE;
      }
      if (arrow === "left") {
        fgStyle.left = MARGIN + 1 - FG_SIZE;
        bgStyle.left = MARGIN - FG_SIZE;
      }
    }

    const { /*color, borderColor,*/ ...propsArrowStyle } = tArrowStyle;

    fgStyle = mergeStyle(fgStyle, propsArrowStyle);
    bgStyle = mergeStyle(bgStyle, propsArrowStyle);

    return { fgStyle, bgStyle };
  }, [arrow, arrowStyle, pos, position]);

  const getPositionStyle = useCallback(() => {
    if (parentEl.current === null) {
      return {};
    }

    let alignOffset = 0;
    const tooltipPosition = parentEl.current.getBoundingClientRect();
    const scrollY =
      window.scrollY !== undefined ? window.scrollY : window.pageYOffset;
    const scrollX =
      window.scrollX !== undefined ? window.scrollX : window.pageXOffset;
    const top = scrollY + tooltipPosition.top;
    const left = scrollX + tooltipPosition.left;
    const _style = {};

    const parentSize = {
      width: parentEl.current.offsetWidth,
      height: parentEl.current.offsetHeight
    };

    // fix for svg
    if (
      !parentEl.current.offsetHeight &&
      parentEl.current.getBoundingClientRect
    ) {
      parentSize.width = parentEl.current.getBoundingClientRect().width;
      parentSize.height = parentEl.current.getBoundingClientRect().height;
    }

    if (align === "left") {
      alignOffset = -parentSize.width / 2 + FG_SIZE;
    } else if (align === "right") {
      alignOffset = parentSize.width / 2 - FG_SIZE;
    }

    const stylesFromPosition = {
      left: () => {
        _style.top = top + parentSize.height / 2 - pos.height / 2;
        _style.left = left - pos.width - MARGIN;
      },
      right: () => {
        _style.top = top + parentSize.height / 2 - pos.height / 2;
        _style.left = left + parentSize.width + MARGIN;
      },
      top: () => {
        _style.left = left - pos.width / 2 + parentSize.width / 2 + alignOffset;
        _style.top = top - pos.height - MARGIN;
      },
      bottom: () => {
        _style.left = left - pos.width / 2 + parentSize.width / 2 + alignOffset;
        _style.top = top + parentSize.height + MARGIN;
      }
    };

    const stylesFromArrow = {
      left: () => {
        _style.left = left + parentSize.width / 2 - MARGIN + alignOffset;
      },
      right: () => {
        _style.left =
          left - pos.width + parentSize.width / 2 + MARGIN + alignOffset;
      },
      top: () => {
        _style.top = top + parentSize.height / 2 - MARGIN;
      },
      bottom: () => {
        _style.top = top + parentSize.height / 2 - pos.height + MARGIN;
      }
    };

    stylesFromPosition[position]?.();
    stylesFromArrow[arrow]?.();

    return _style;
  }, [parentEl, align, position, arrow, pos.height, pos.width]);

  const finalStyles = useMemo(() => {
    if (parentEl.current === undefined) {
      return {};
    }

    let _style = mergeStyle(
      {
        position: "absolute",
        fontSize: "0.85rem",
        maxWidth: "25rem",
        background: "#fff",
        color: "#333",
        padding: "1.5rem 2rem",
        boxShadow: "3px 3px 5px rgba(0,0,0,.5)",
        borderRadius: "3px",
        zIndex: 2000,
        ...getPositionStyle()
      },
      style
    );

    let tArrowStyle = getArrowStyle();
    if (position === "top" || position === "bottom") {
      if (_style.left < 0) {
        if (pos.width > 0) {
          const tooltipWidth = pos.width;
          const bgStyleRight =
            tArrowStyle.bgStyle.right || tooltipWidth / 2 - BG_SIZE;
          const newBgRight = Math.round(bgStyleRight - _style.left + MARGIN);
          tArrowStyle = {
            ...tArrowStyle,
            bgStyle: {
              ...tArrowStyle.bgStyle,
              right: newBgRight,
              left: null
            },
            fgStyle: {
              ...tArrowStyle.fgStyle,
              right: newBgRight + 1,
              left: null
            }
          };
        }
        _style.left = MARGIN;
      } else {
        const rightOffset = _style.left + pos.width - window.innerWidth;
        if (rightOffset > 0) {
          const originalLeft = _style.left;
          _style.left = window.innerWidth - pos.width - MARGIN;
          tArrowStyle.fgStyle.marginLeft += originalLeft - _style.left;
          tArrowStyle.bgStyle.marginLeft += originalLeft - _style.left;
        }
      }
    }

    return { style: _style, arrowStyle: tArrowStyle };
  }, [parentEl, getPositionStyle, style, getArrowStyle, position, pos.width]);

  //console.log("Tooltip card finalStyles", finalStyles);

  useEffect(() => {
    if (rootRef.current) {
      const newWidth = rootRef.current.offsetWidth;
      const newHeight = rootRef.current.offsetHeight;

      setPos({
        width: newWidth,
        height: newHeight
      });
    }
  }, []);

  return (
    <motion.div
      style={finalStyles.style}
      ref={rootRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {arrow ? (
        <div>
          <span style={finalStyles.arrowStyle.fgStyle} />
          <span style={finalStyles.arrowStyle.bgStyle} />
        </div>
      ) : null}
      {children}
    </motion.div>
  );
};

export default Card;
